import api from './api';
import objectToQuery from '/lib/utils/objectToQuery';
import { TimeSelectorResourceType } from '/types/resourceType';

const projectApi = {
    create: (projectData) => api.post('project', projectData),

    getTimeSlots: ({ workHours, postCode, page, type = 'project', cartUuid }) =>
        api.post(
            'schedule/availability',
            {
                work_hours: workHours,
                post_code: postCode,
                type: type,
                page: page,
                cart_uuid: cartUuid,
            },
            {}
        ),

    reserveTimeslot: ({
        timeslotUuid,
        cartUuid,
        type = TimeSelectorResourceType.PROJECT,
    }) => {
        return api.post(
            'schedule/reserve',
            { timeslot_uuid: timeslotUuid, entity_uuid: cartUuid, type },
            {}
        );
    },

    getFirstAvailableTimeSlot: (workHours, postCode) =>
        api.post('schedule/first-available', {
            work_hours: workHours,
            post_code: postCode,
        }),

    getAll: ({ token, params = {} }) =>
        api.getSecure(`project?${objectToQuery(params)}`, token),

    getAllByTab: ({
        tab,
        group,
        token,
        page,
        caseworkerUuid,
        electricianUuid,
        query,
        orderBy,
        order,
    }) =>
        api.getSecure(
            `project/tab/${group}/${tab}?page=${page}&per_page=15${
                caseworkerUuid ? '&caseworker_uuid=' + caseworkerUuid : ''
            }${electricianUuid ? '&electrician_uuid=' + electricianUuid : ''}${
                query ? '&query=' + query : ''
            }&order-by=${orderBy}&order=${order}`,
            token
        ),

    getAllByTabWithClaims: ({
        tab,
        token,
        page,
        query,
        orderBy,
        order,
        caseworkerUuid,
        electricianUuid,
    }) =>
        api.getSecure(
            `project/tab/with-claims/${tab}?page=${page}&per_page=15${
                query ? '&query=' + query : ''
            }&order-by=${orderBy}&order=${order}${
                caseworkerUuid ? '&caseworker_uuid=' + caseworkerUuid : ''
            }${electricianUuid ? '&electrician_uuid=' + electricianUuid : ''}`,
            token
        ),

    getByRef: (ref, token, headers) =>
        api.getSecure('project/ref/' + ref, token, headers),

    getActivityLog: (projectId) =>
        api.getSecure(`project/${projectId}/activity-log`),

    getByDate: (date, electricianUuid) =>
        api.post(`project/date`, { date, electrician_uuid: electricianUuid }),

    getConcerns: ({ group, caseworkerUuid, electricianUuid }) =>
        api.getSecure(
            `project/tab-concerns/${group}?${
                caseworkerUuid ? '&caseworker_uuid=' + caseworkerUuid : ''
            }${electricianUuid ? '&electrician_uuid=' + electricianUuid : ''}`
        ),

    regenerateReceipt: (projectId) =>
        api.post(`project/${projectId}/regenerate-receipt`),

    clearImageConcern: (projectId) =>
        api.post(`project/${projectId}/clear-image-concern`),

    clearCustomerFeedbackConcern: (projectId, message) =>
        api.post(`project/${projectId}/clear-customer-feedback-concern`, {
            message,
        }),

    resolveEvaluationComplaint: (projectId, message) =>
        api.post(`project/${projectId}/evaluation-complaint/resolve`, {
            message,
        }),

    clearContactCustomerConcern: (projectId, message) =>
        api.post(`project/${projectId}/clear-contact-customer-concern`, {
            message,
        }),

    clearExpiredMaterialConcern: (projectId) =>
        api.post(`project/${projectId}/clear-expired-material-concern`),

    clearBusinessCustomerConcern: (projectId) =>
        api.post(`project/${projectId}/clear-business-customer-concern`),

    schedule: (projectId, dateTime, electricianId) =>
        api.post(`project/${projectId}/schedule`, {
            installation_date: dateTime,
            electrician_id: electricianId,
        }),
    bookReservation: (projectId, reservationUuid) =>
        api.post(`project/${projectId}/schedule`, {
            reservation_uuid: reservationUuid,
        }),

    cancelJob: (projectId, jobId, reason) =>
        api.post(`project/${projectId}/line-item/${jobId}/cancel`, { reason }),

    restoreCancelledJob: (projectId, jobId) =>
        api.post(`project/${projectId}/line-item/${jobId}/re-enable`, {}),

    cancel: (projectId, message = '', refund = false) =>
        api.post(`project/${projectId}/cancel`, {
            message,
            ...(refund ? { refund_up_front_payment: true } : {}),
        }),

    restore: (projectId) => api.post(`project/${projectId}/re-enable`, {}),

    delete: (projectId) => api.post(`project/${projectId}/delete`, {}),

    complete: (projectId) =>
        api.post(`project/${projectId}/complete-installation`),

    evaluate: (projectId, data) =>
        api.patch(`project/${projectId}/evaluate`, data),

    stopPrePaymentProcess: (projectId, message) =>
        api.post(`project/${projectId}/pre-payment-process/stop`, { message }),

    restartPrePaymentProcess: (projectId, message) =>
        api.post(`project/${projectId}/pre-payment-process/restart`, {
            message,
        }),

    manuallyExpirePrePaymentProcess: (projectId, invoiceFee = true) =>
        api.post(`project/${projectId}/pre-payment-process/expire`, {
            invoice_fee: invoiceFee,
        }),

    sendDiscountCode: (project_id, phone_number, referral_channels) =>
        api.post(`discount/refer`, {
            project_id,
            phone_number,
            referral_channels,
        }),

    checkDiscountCode: (code) => api.get(`discount/${code}`),

    sendPaymentLink: (projectId, message) =>
        api.post(`project/${projectId}/send-payment-link`, { message }),

    createLineItem: (projectId, lineItem) =>
        api.post(`project/${projectId}/line-item`, lineItem),

    updateLineItem: (projectId, lineItemId, lineItem) =>
        api.patch(`project/${projectId}/line-item/${lineItemId}`, lineItem),

    deleteLineItem: (projectId, lineItemId) =>
        api.delete(`project/${projectId}/line-item/${lineItemId}`),

    priceChangeNotify: (projectId) =>
        api.post(`project/${projectId}/notify-price-change`),

    rescheduleElectricians: (projectId, params) =>
        api.getSecure(
            `project/${projectId}/reschedule/electrician?${objectToQuery(
                params
            )}`
        ),

    rescheduleContractors: (projectId) =>
        api.getSecure(`project/${projectId}/reschedule/contractor`),

    rescheduleTimeslots: (projectId, electricianUuid, page) =>
        api.getSecure(
            `project/${projectId}/reschedule/electrician/${electricianUuid}?page=${
                page || 1
            }`
        ),

    reschedule: (projectId, timeslotUuid) =>
        api.post(`project/${projectId}/reschedule`, {
            timeslot_uuid: timeslotUuid,
        }),

    rescheduleAvailability: (projectId, params) =>
        api.getSecure(
            `project/${projectId}/reschedule/availability?${objectToQuery(
                params
            )}`
        ),

    getProjectTimeSlots: (projectId, page, cartUuid) =>
        api.post(`project/${projectId}/schedule/availability`, {
            page,
            cart_uuid: cartUuid,
        }),

    scheduleProject: (projectId, reservationUuid) =>
        api.post(`project/${projectId}/schedule`, {
            reservation_uuid: reservationUuid,
        }),

    confirmSalesOrder: (projectId, lineItemId) =>
        api.post(`project/${projectId}/line-item/${lineItemId}/sales-order`),

    updateCaseworker: (projectId, caseworkerUuid) =>
        api.post(`project/${projectId}/update-caseworker`, {
            caseworker_uuid: caseworkerUuid,
        }),

    addComment: (projectId, message) =>
        api.post(`project/${projectId}/comment`, { message }),

    update: (projectId, project) => api.patch(`project/${projectId}`, project),

    createClaimRequest: (projectId, message) =>
        api.post(`project/${projectId}/claim-request`, { message }),

    resolveClaimRequest: (projectId, message) =>
        api.post(`project/${projectId}/claim-request/resolve`, { message }),

    createEvaluationComplaint: (projectId) =>
        api.post(`project/${projectId}/evaluation-complaint`),

    requestContact: (projectId, data) =>
        api.post(`project/${projectId}/request-contact`, data),

    createOffer: (projectId) => api.post(`project/${projectId}/offer`),

    table: (query) => api.post(`project/table`, query),

    draft: {
        create: (ref) => api.post(`project/${ref}/draft`),

        delete: (projectId) => api.delete(`project/${projectId}/draft`),

        decline: (projectId) => api.post(`project/${projectId}/draft/decline`),

        accept: (projectId) =>
            api.post(`project/${projectId}/draft/accept-change`),

        get: (ref) => api.getSecure(`project/${ref}/draft`),

        createLineItem: (projectId, lineItem) =>
            api.post(`project/${projectId}/draft/line-item`, lineItem),

        updateLineItem: (projectId, lineItemId, lineItem) =>
            api.patch(
                `project/${projectId}/draft/line-item/${lineItemId}`,
                lineItem
            ),

        updateLineItemName: (projectId, lineItemId, name) =>
            api.patch(
                `project/${projectId}/draft/line-item/${lineItemId}/name`,
                { name }
            ),

        deleteLineItem: (projectId, lineItemId) =>
            api.delete(`project/${projectId}/draft/line-item/${lineItemId}`),

        cancelJob: (projectId, jobId, reason) =>
            api.post(`project/${projectId}/draft/line-item/${jobId}/cancel`, {
                reason,
            }),

        restoreCancelledJob: (projectId, jobId) =>
            api.post(
                `project/${projectId}/draft/line-item/${jobId}/re-enable`,
                {}
            ),

        merge: (projectId) => api.post(`project/${projectId}/draft/merge `),

        sendForApproval: (projectId) =>
            api.post(`project/${projectId}/draft/send-for-approval`),

        getDrafts: (projectId) =>
            api.getSecure(`project/${projectId}/draft/list`),

        getDraftById: (projectId, draftId) =>
            api.getSecure(`project/${projectId}/draft/${draftId}`),

        updateLineItemOrder: (projectId, idOrderMapping) =>
            api.post(
                `project/${projectId}/draft/line-item/order`,
                idOrderMapping
            ), // { id: order }
    },

    concern: {
        create: ({ projectId, comment }) =>
            api.post(`project/${projectId}/concern/create/custom_concern`, {
                data: { comment: comment || '' },
            }),

        solve: ({ projectId, concernType, solveCommnet }) =>
            api.post(`project/${projectId}/concern/solve/${concernType}`, {
                data: { solve_comment: solveCommnet || '' },
            }),

        delete: ({ projectId, concernType }) =>
            api.delete(`project/${projectId}/concern/delete/${concernType}`),
    },

    physicalInspection: {
        rescheduleAvailability: (projectId, params) =>
            api.getSecure(
                `project/${projectId}/physical-inspection/reschedule/availability?${objectToQuery(
                    params
                )}`
            ),

        rescheduleElectricians: (projectId, params) =>
            api.getSecure(
                `project/${projectId}/physical-inspection/reschedule/electrician?${objectToQuery(
                    params
                )}`
            ),

        rescheduleContractors: (projectId) =>
            api.getSecure(
                `project/${projectId}/physical-inspection/reschedule/contractor`
            ),

        reschedule: (projectId, timeslotUuid) =>
            api.post(`project/${projectId}/physical-inspection/reschedule`, {
                timeslot_uuid: timeslotUuid,
            }),
        complete: (projectId, data) =>
            api.post(`project/${projectId}/physical-inspection/complete`, data),
        schedule: (projectId, reservationUuid) =>
            api.post(`project/${projectId}/physical-inspection/schedule`, {
                reservation_uuid: reservationUuid,
            }),
    },

    application: {
        sent: (projectId) => api.post(`project/${projectId}/application/sent`),
        accepted: (projectId) =>
            api.post(`project/${projectId}/application/accepted`),
        declined: (projectId) =>
            api.post(`project/${projectId}/application/declined`),
    },

    updateElectricianInfo: (projectId, message) =>
        api.patch(`project/${projectId}/electrician-info`, {
            electrician_info: message,
        }),

    updateExpireDate: (projectId, expireDate) =>
        api.patch(`project/${projectId}/expire-date`, {
            expires_at: expireDate,
        }),
};

export default projectApi;
